import React from 'react'
import {useSelector} from 'react-redux'
import {AvailabilityComponent} from 'components/AvailabilityDatePicker/useAvailability'
import {useCashbackInfoClick} from 'components/Cashback/useCashbackInfoClick'
import {useTrackPreferredRateOfferServed} from 'hooks/useTrackPreferredRateOfferServed'
import {getOfferAccessFlowSettings} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {
  Entity,
  Page,
  PerformanceMetric
} from '@vio/core/src/analytics/types/Events'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel, RateChecker} from '@vio/core/src/offer/types/offer'
import {OfferPrimaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {OfferVariant} from '@vio/core/src/offer/types/OfferVariant'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'
import {OfferLayout} from '@vio/shared/src/search/OfferLayout'
import TrackPerformanceTiming from '@vio/shared/src/utils/PerformanceMonitor/TrackPerformanceTiming'
import {EarnPoints} from '@vio/shared/src/whiteLabel/EarnPoints'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'

import {useOfferHasTag} from '../hooks/useOfferHasTag'
import {OfferInteraction} from '../Shared/OfferInteraction'
import {ProviderLogoComponent} from '../Shared/ProviderLogoComponent'
import {RoomLabelsComponent} from '../Shared/RoomLabelsComponent'
import {SplitBookingOfferInteraction} from '../Shared/SplitBookingOfferInteraction'
import {CtaComponent} from './CtaComponent'
import {PriceComponent} from './PriceComponent'
import {SplitBookingPrimaryOffer} from './SplitBookingPrimaryOffer'
import {TagComponent} from './TagComponent/TagComponent'

interface MainProps {
  isLoading?: false
  variant: OfferPrimaryLayoutVariant
  hotelId: string
  offer: SearchOffer
  anchorPrice: number
  anchorPriceTotal?: number
  offerVariant?: OfferVariant
  hasRoomLabels?: boolean
  isSplitBooking?: false
  isYourChoice?: boolean
  shouldShowTaxation?: boolean
  shouldShowTaxationAndNightly?: boolean
  showPriceIndication?: boolean
  sourceComponent?: string
  offerPosition?: number
  hasInteraction: boolean
  splitBookingOffers?: never
  splitBookingBundle?: never
  hasWeb2AppPromo?: boolean
  showCashback?: boolean
  showProviderLogo?: boolean
  showCta?: never
}

interface LoadingProps {
  isLoading: true
  variant: OfferPrimaryLayoutVariant
  hasInteraction?: false
  hotelId?: never
  offer?: never
  splitBookingOffers?: never
  splitBookingBundle?: never
  anchorPrice?: never
  anchorPriceTotal?: never
  offerVariant?: never
  isSplitBooking?: boolean
  shouldShowTaxation?: never
  shouldShowTaxationAndNightly?: never
  showPriceIndication?: never
  hasRoomLabels?: never
  sourceComponent?: never
  offerPosition?: never
  hasWeb2AppPromo?: never
  showCashback?: boolean
  showProviderLogo?: boolean
  showCta?: never
}

type SplitBookingProps = React.ComponentProps<typeof SplitBookingPrimaryOffer>

export const PrimaryOffer: React.FC<
  MainProps | SplitBookingProps | LoadingProps
> = ({
  variant = 'PrimaryDefault',
  hotelId,
  offer,
  splitBookingOffers = [],
  splitBookingBundle,
  offerVariant,
  anchorPrice,
  isLoading = false,
  isSplitBooking,
  offerPosition = 0,
  shouldShowTaxation = true,
  shouldShowTaxationAndNightly = false,
  anchorPriceTotal,
  hasRoomLabels = true,
  showPriceIndication = false,
  sourceComponent,
  hasInteraction = true,
  hasWeb2AppPromo = false,
  showCashback,
  showProviderLogo = true,
  showCta
}) => {
  const {isAppRequired} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const offerHasTag = useOfferHasTag(offerVariant)
  const isYourChoice = hasOfferTag(
    offer || splitBookingBundle,
    OfferTagLabel.IsYourChoice
  )
  useTrackPreferredRateOfferServed(isYourChoice)
  const onCashbackInfoClick = useCashbackInfoClick()
  const {canEarn: hasEarnPoints} = usePointsPriceDisplay()

  if (isLoading) {
    return <OfferLayout variant={variant} isLoading />
  }

  if (isSplitBooking) {
    return (
      <SplitBookingOfferInteraction
        component={sourceComponent}
        hotelId={hotelId}
      >
        <Box
          display="inline"
          data-id={isYourChoice && RateChecker.GHAYourChoiceId}
        >
          <SplitBookingPrimaryOffer
            isSplitBooking
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            variant={variant}
            hotelId={hotelId}
            splitBookingOffers={splitBookingOffers}
            offerVariant={offerVariant}
            shouldShowTaxation={shouldShowTaxation}
            shouldShowTaxationAndNightly={shouldShowTaxationAndNightly}
            hasRoomLabels={hasRoomLabels}
            showCashback={showCashback}
            splitBookingBundle={splitBookingBundle}
            showProviderLogo={showProviderLogo}
            showCta={showCta}
          />
        </Box>
      </SplitBookingOfferInteraction>
    )
  }
  return (
    <OfferInteraction
      anchorPriceTotal={anchorPriceTotal}
      hotelId={hotelId}
      offer={offer}
      component={sourceComponent}
      offerPosition={offerPosition}
      hasInteraction={hasInteraction}
    >
      <TrackPerformanceTiming
        entity={Entity.Offer}
        name={PerformanceMetric.LRP}
        page={Page.SearchResults}
      />
      <Box
        display="inline"
        data-id={isYourChoice && RateChecker.GHAYourChoiceId}
      >
        <OfferLayout
          variant={variant}
          isLoading={isLoading}
          tagComponent={
            offerHasTag ? (
              <TagComponent
                anchorPrice={anchorPrice}
                anchorPriceTotal={anchorPriceTotal}
                offer={offer}
                offerVariant={offerVariant}
                variant={variant}
              />
            ) : null
          }
          cashbackComponent={
            showCashback && offer.cashback ? (
              <Box paddingTop="s100">
                <Cashback
                  value={offer.cashback.value}
                  type={offer.cashback.type}
                  withInfoAction
                  onInfoClick={onCashbackInfoClick}
                />
              </Box>
            ) : hasEarnPoints ? (
              <Box paddingTop="s300">
                <EarnPoints amount={offer.totalRate} />
              </Box>
            ) : null
          }
          priceComponent={
            <PriceComponent
              hotelId={hotelId}
              anchorPrice={anchorPrice}
              anchorPriceTotal={anchorPriceTotal}
              offer={offer}
              offerVariant={offerVariant}
              shouldShowTaxation={shouldShowTaxation}
              shouldShowTaxationAndNightly={shouldShowTaxationAndNightly}
              variant={variant}
              showPriceIndication={showPriceIndication}
              forceMobileLayout={
                sourceComponent ===
                AvailabilityComponent.DesktopAvailabilityCalendar
              }
            />
          }
          providerLogoComponent={
            showProviderLogo ? (
              <ProviderLogoComponent
                hotelId={hotelId}
                offer={offer}
                variant={variant}
                isAppLockedDeal={isAppRequired}
              />
            ) : undefined
          }
          roomLabelsComponent={
            <RoomLabelsComponent
              offer={offer}
              hasRoomLabels={hasRoomLabels}
              hotelId={hotelId}
            />
          }
          ctaComponent={
            <CtaComponent
              offer={offer}
              hasWeb2AppPromo={hasWeb2AppPromo}
              hotelId={hotelId}
              variant={variant}
            />
          }
        />
      </Box>
    </OfferInteraction>
  )
}
